import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiPlus, FiShoppingCart, FiFileText, FiDownload,
  FiBell, FiX, FiCheck, FiAlertCircle, FiInfo, FiClock,
  FiSun, FiMoon, FiCommand, FiArrowRight, FiCornerDownLeft,
  FiSettings, FiUsers, FiBookOpen, FiDollarSign, FiPackage,
  FiTruck, FiBarChart2, FiCreditCard, FiHome, FiGrid, FiBook,
  FiMapPin, FiBriefcase, FiUserCheck, FiLayers
} from 'react-icons/fi';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import { cardVariants, bellShakeVariants, drawerVariants } from '../../theme/animations';
import { getTimeBasedGreeting, formatRelativeDate } from '../../utils/formatters';
import { useDashboard } from '../../context/DashboardContext';
import useExportPDF from '../../hooks/useExportPDF';
import { useAuth } from '../../context/AuthContext';
import {
  getCustomersCached, getVendorsCached, getAccountsCached, getItemsCached,
} from '../../utils/masterDataStore';

// ═══════════════════════════════════════════════════════
// COMMAND PALETTE SEARCH INDEX
// ═══════════════════════════════════════════════════════
const SEARCH_INDEX = [
  // Dashboard
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', category: 'Navigation', icon: 'home', keywords: ['home', 'main', 'overview', 'dashboard'] },

  // Settings
  { id: 'my-company', label: 'My Company', path: '/settings/company', category: 'Settings', icon: 'settings', keywords: ['company', 'profile', 'settings', 'my company', 'company settings'] },
  { id: 'users-roles', label: 'Users & Roles', path: '/settings/users', category: 'Settings', icon: 'users', keywords: ['users', 'roles', 'permissions', 'team', 'staff'] },
  { id: 'financial-year', label: 'Financial Year', path: '/settings/financial-year', category: 'Settings', icon: 'settings', keywords: ['financial year', 'fiscal year', 'year', 'fy'] },
  { id: 'document-settings', label: 'Document Settings', path: '/settings/documents', category: 'Settings', icon: 'settings', keywords: ['document', 'numbering', 'prefix', 'series', 'doc settings'] },

  // Master Data
  { id: 'chart-of-accounts', label: 'Chart of Accounts', path: '/master/chart-of-accounts', category: 'Master Data', icon: 'book', keywords: ['coa', 'chart of accounts', 'accounts', 'ledger accounts', 'gl', 'general ledger'] },
  { id: 'customers', label: 'Customers', path: '/master/customers', category: 'Master Data', icon: 'users', keywords: ['customers', 'client', 'buyer', 'party', 'debtors'] },
  { id: 'vendors', label: 'Vendors', path: '/master/vendors', category: 'Master Data', icon: 'truck', keywords: ['vendors', 'supplier', 'creditors', 'party'] },
  { id: 'banks', label: 'Banks', path: '/master/banks', category: 'Master Data', icon: 'credit-card', keywords: ['banks', 'bank accounts', 'banking'] },
  { id: 'inventory', label: 'Chart of Inventory', path: '/master/chart-of-inventory', category: 'Master Data', icon: 'package', keywords: ['inventory', 'items', 'products', 'stock', 'chart of inventory', 'coi'] },
  { id: 'locations', label: 'Inventory Locations', path: '/master/inventory-locations', category: 'Master Data', icon: 'map-pin', keywords: ['locations', 'warehouse', 'godown', 'store locations'] },
  { id: 'employees', label: 'Employees', path: '/master/employees', category: 'Master Data', icon: 'user-check', keywords: ['employees', 'staff', 'salesman', 'salesperson'] },
  { id: 'regions', label: 'Regions', path: '/master/regions', category: 'Master Data', icon: 'map-pin', keywords: ['regions', 'areas', 'territory', 'zone'] },
  { id: 'jobs', label: 'Jobs / Projects', path: '/master/jobs', category: 'Master Data', icon: 'briefcase', keywords: ['jobs', 'projects', 'work orders', 'costings'] },
  { id: 'opening-balances', label: 'Opening Balances', path: '/master/opening-balances', category: 'Master Data', icon: 'dollar', keywords: ['opening balance', 'ob', 'opening', 'beginning balance'] },

  // Purchases
  { id: 'purchase-order', label: 'Purchase Order', path: '/purchases/purchase-order', category: 'Purchases', icon: 'file', keywords: ['purchase order', 'po', 'buy order', 'new purchase order'] },
  { id: 'grn', label: 'GRN (Goods Receipt)', path: '/purchases/grn', category: 'Purchases', icon: 'file', keywords: ['grn', 'goods receipt', 'goods received', 'receiving'] },
  { id: 'purchase-invoice', label: 'Purchase Invoice', path: '/purchases/purchase-invoice', category: 'Purchases', icon: 'file', keywords: ['purchase invoice', 'pi', 'purchase bill', 'buy', 'new purchase'] },
  { id: 'purchase-return', label: 'Purchase Return', path: '/purchases/purchase-return', category: 'Purchases', icon: 'file', keywords: ['purchase return', 'pr', 'debit note', 'return to vendor'] },

  // Sales
  { id: 'quotation', label: 'Quotation', path: '/sales/quotation', category: 'Sales', icon: 'file', keywords: ['quotation', 'qt', 'quote', 'estimate', 'proforma'] },
  { id: 'sale-order', label: 'Sale Order', path: '/sales/sale-order', category: 'Sales', icon: 'file', keywords: ['sale order', 'so', 'sales order', 'order'] },
  { id: 'delivery-challan', label: 'Delivery Challan', path: '/sales/delivery-challan', category: 'Sales', icon: 'file', keywords: ['delivery challan', 'dc', 'challan', 'delivery note', 'dispatch'] },
  { id: 'sale-invoice', label: 'Sale Invoice', path: '/sales/sale-invoice', category: 'Sales', icon: 'file', keywords: ['sale invoice', 'si', 'sales invoice', 'invoice', 'bill', 'new sale', 'new invoice'] },
  { id: 'sale-return', label: 'Sale Return', path: '/sales/sale-return', category: 'Sales', icon: 'file', keywords: ['sale return', 'sr', 'credit note', 'return from customer'] },

  // POS
  { id: 'pos', label: 'POS Screen', path: '/pos', category: 'Sales', icon: 'grid', keywords: ['pos', 'point of sale', 'cash sale', 'counter sale', 'retail'] },

  // Store
  { id: 'purchase-requisition', label: 'Purchase Requisition', path: '/store/purchase-requisition', category: 'Store', icon: 'package', keywords: ['purchase requisition', 'requisition', 'request', 'indent'] },
  { id: 'inward-gate-pass', label: 'Inward Gate Pass', path: '/store/inward-gate-pass', category: 'Store', icon: 'package', keywords: ['inward gate pass', 'igp', 'inward', 'gate pass in'] },
  { id: 'outward-gate-pass', label: 'Outward Gate Pass', path: '/store/outward-gate-pass', category: 'Store', icon: 'package', keywords: ['outward gate pass', 'ogp', 'outward', 'gate pass out'] },
  { id: 'bom', label: 'Bill of Materials', path: '/store/bill-of-materials', category: 'Store', icon: 'layers', keywords: ['bom', 'bill of materials', 'recipe', 'formula'] },
  { id: 'production-order', label: 'Production Order', path: '/store/production-order', category: 'Store', icon: 'package', keywords: ['production order', 'production', 'manufacturing', 'assembly'] },
  { id: 'add-stock', label: 'Add Stock', path: '/store/add-stock', category: 'Store', icon: 'package', keywords: ['add stock', 'stock in', 'stock adjustment', 'increase stock'] },
  { id: 'reduce-stock', label: 'Reduce Stock', path: '/store/reduce-stock', category: 'Store', icon: 'package', keywords: ['reduce stock', 'stock out', 'stock adjustment', 'decrease stock'] },
  { id: 'stock-transfer', label: 'Stock Transfer', path: '/store/stock-transfer', category: 'Store', icon: 'package', keywords: ['stock transfer', 'transfer', 'move stock', 'warehouse transfer'] },

  // Receipts
  { id: 'cash-receipt', label: 'Cash Receipt', path: '/receipts/cash-receipt', category: 'Receipts', icon: 'dollar', keywords: ['cash receipt', 'cr', 'cash received', 'cash collection', 'receive cash'] },
  { id: 'bank-receipt', label: 'Bank Receipt', path: '/receipts/bank-receipt', category: 'Receipts', icon: 'dollar', keywords: ['bank receipt', 'br', 'bank received', 'cheque received', 'bank collection'] },

  // Payments
  { id: 'cash-payment', label: 'Cash Payment', path: '/payments/cash-payment', category: 'Payments', icon: 'credit-card', keywords: ['cash payment', 'cp', 'pay cash', 'cash paid'] },
  { id: 'bank-payment', label: 'Bank Payment', path: '/payments/bank-payment', category: 'Payments', icon: 'credit-card', keywords: ['bank payment', 'bp', 'pay bank', 'cheque payment', 'bank paid'] },

  // Journal
  { id: 'general-journal', label: 'General Journal', path: '/journal/general', category: 'Journal', icon: 'book', keywords: ['journal', 'jv', 'journal voucher', 'general journal', 'journal entry'] },

  // Reports - Financial
  { id: 'ledger', label: 'Account Ledger', path: '/reports/ledger', category: 'Reports', icon: 'bar-chart', keywords: ['ledger', 'account ledger', 'party ledger', 'gl', 'statement'] },
  { id: 'trial-balance', label: 'Trial Balance', path: '/reports/trial-balance', category: 'Reports', icon: 'bar-chart', keywords: ['trial balance', 'tb', 'trial'] },
  { id: 'profit-loss', label: 'Profit & Loss', path: '/reports/profit-loss', category: 'Reports', icon: 'bar-chart', keywords: ['profit loss', 'p&l', 'pl', 'income statement', 'profit and loss', 'pnl'] },
  { id: 'balance-sheet', label: 'Balance Sheet', path: '/reports/balance-sheet', category: 'Reports', icon: 'bar-chart', keywords: ['balance sheet', 'bs', 'financial position'] },
  { id: 'cash-flow-report', label: 'Cash Flow Statement', path: '/reports/cash-flow', category: 'Reports', icon: 'bar-chart', keywords: ['cash flow', 'cash flow statement', 'cf'] },
  { id: 'tax-reports', label: 'Tax Reports', path: '/reports/tax-reports', category: 'Reports', icon: 'bar-chart', keywords: ['tax', 'tax report', 'gst', 'sales tax', 'fbr'] },

  // Reports - General
  { id: 'journal-report', label: 'Journal Report', path: '/reports/journal', category: 'Reports', icon: 'bar-chart', keywords: ['journal report', 'day book', 'journal book'] },
  { id: 'vouchers', label: 'Vouchers', path: '/reports/vouchers', category: 'Reports', icon: 'bar-chart', keywords: ['vouchers', 'voucher report', 'voucher list'] },
  { id: 'serial-tracking', label: 'Serial Tracking', path: '/reports/serial-tracking', category: 'Reports', icon: 'bar-chart', keywords: ['serial', 'serial tracking', 'serial number'] },
  { id: 'po-tracking', label: 'PO Tracking', path: '/reports/po-tracking', category: 'Reports', icon: 'bar-chart', keywords: ['po tracking', 'purchase order tracking', 'po status'] },
  { id: 'so-tracking', label: 'SO Tracking', path: '/reports/so-tracking', category: 'Reports', icon: 'bar-chart', keywords: ['so tracking', 'sale order tracking', 'so status'] },

  // Reports - Purchase
  { id: 'purchase-summary', label: 'Purchase Summary', path: '/reports/purchase-summary', category: 'Reports', icon: 'bar-chart', keywords: ['purchase summary', 'purchase report'] },
  { id: 'purchase-register', label: 'Purchase Register', path: '/reports/purchase-register', category: 'Reports', icon: 'bar-chart', keywords: ['purchase register', 'purchase list'] },
  { id: 'party-purchase-summary', label: 'Party Purchase Summary', path: '/reports/party-purchase-summary', category: 'Reports', icon: 'bar-chart', keywords: ['party purchase', 'vendor purchase', 'supplier purchase'] },
  { id: 'vendor-payments-report', label: 'Vendor Payments', path: '/reports/vendor-payments', category: 'Reports', icon: 'bar-chart', keywords: ['vendor payments', 'supplier payments', 'payment history'] },
  { id: 'purchase-activity', label: 'Purchase Activity', path: '/reports/purchase-activity', category: 'Reports', icon: 'bar-chart', keywords: ['purchase activity'] },
  { id: 'item-purchase-analysis', label: 'Item Purchase Analysis', path: '/reports/item-purchase-analysis', category: 'Reports', icon: 'bar-chart', keywords: ['item purchase', 'product purchase', 'item wise purchase'] },
  { id: 'ap-aging', label: 'AP Aging', path: '/reports/ap-aging', category: 'Reports', icon: 'bar-chart', keywords: ['ap aging', 'accounts payable aging', 'payable aging', 'creditor aging'] },

  // Reports - Sale
  { id: 'sale-summary', label: 'Sale Summary', path: '/reports/sale-summary', category: 'Reports', icon: 'bar-chart', keywords: ['sale summary', 'sales summary', 'sale report', 'sales report', 'today sale', 'today sales'] },
  { id: 'sale-register', label: 'Sale Register', path: '/reports/sale-register', category: 'Reports', icon: 'bar-chart', keywords: ['sale register', 'sales register', 'sale list', 'today sale report'] },
  { id: 'party-sale-summary', label: 'Party Sale Summary', path: '/reports/party-sale-summary', category: 'Reports', icon: 'bar-chart', keywords: ['party sale', 'customer sale', 'customer wise sale'] },
  { id: 'collection-report', label: 'Collection Report', path: '/reports/collection-report', category: 'Reports', icon: 'bar-chart', keywords: ['collection', 'collection report', 'receivables collected'] },
  { id: 'sale-activity', label: 'Sale Activity', path: '/reports/sale-activity', category: 'Reports', icon: 'bar-chart', keywords: ['sale activity', 'sales activity'] },
  { id: 'item-sale-analysis', label: 'Item Sale Analysis', path: '/reports/item-sale-analysis', category: 'Reports', icon: 'bar-chart', keywords: ['item sale', 'product sale', 'item wise sale'] },
  { id: 'ar-aging', label: 'AR Aging', path: '/reports/ar-aging', category: 'Reports', icon: 'bar-chart', keywords: ['ar aging', 'accounts receivable aging', 'receivable aging', 'debtor aging'] },
  { id: 'salesperson-performance', label: 'Salesperson Performance', path: '/reports/salesperson-performance', category: 'Reports', icon: 'bar-chart', keywords: ['salesperson', 'salesman', 'performance', 'sales performance'] },

  // Reports - Inventory
  { id: 'low-stock-alert', label: 'Low Stock Alert', path: '/reports/low-stock-alert', category: 'Reports', icon: 'bar-chart', keywords: ['low stock', 'reorder', 'out of stock', 'stock alert'] },

  // Reports - Intelligence
  { id: 'financial-health', label: 'Financial Health Report', path: '/reports/financial-health', category: 'Reports', icon: 'bar-chart', keywords: ['financial health', 'health report'] },
  { id: 'sales-intelligence', label: 'Sales Intelligence', path: '/reports/sales-intelligence', category: 'Reports', icon: 'bar-chart', keywords: ['sales intelligence', 'sales analytics'] },
  { id: 'inventory-intelligence', label: 'Inventory Intelligence', path: '/reports/inventory-intelligence', category: 'Reports', icon: 'bar-chart', keywords: ['inventory intelligence', 'inventory analytics'] },
  { id: 'cash-flow-management', label: 'Cash Flow Management', path: '/reports/cash-flow-management', category: 'Reports', icon: 'bar-chart', keywords: ['cash flow management', 'cash management'] },
  { id: 'operational-metrics', label: 'Operational Metrics', path: '/reports/operational-metrics', category: 'Reports', icon: 'bar-chart', keywords: ['operational metrics', 'operations', 'kpi'] },

  // Help
  { id: 'help', label: 'Help & User Manual', path: '/help', category: 'Navigation', icon: 'info', keywords: ['help', 'manual', 'guide', 'support', 'how to'] },
];

// Smart shortcut patterns - maps natural language to actions
const SMART_PATTERNS = [
  // "{name} ledger" → Go to ledger (the name will be searched as a Firestore entity)
  { pattern: /^(.+?)\s+ledger$/i, action: 'ledger', label: (m) => `${m[1]} - Account Ledger`, path: '/reports/ledger' },
  // "{name} statement" → Go to ledger
  { pattern: /^(.+?)\s+statement$/i, action: 'ledger', label: (m) => `${m[1]} - Account Statement`, path: '/reports/ledger' },
  // "today sale" or "today sales" → Sale register
  { pattern: /^today\s+sale[s]?(\s+report)?$/i, action: 'navigate', label: () => 'Today\'s Sale Report', path: '/reports/sale-register' },
  // "today purchase" → Purchase register
  { pattern: /^today\s+purchase[s]?(\s+report)?$/i, action: 'navigate', label: () => 'Today\'s Purchase Report', path: '/reports/purchase-register' },
  // "today collection" → Collection report
  { pattern: /^today\s+collection[s]?$/i, action: 'navigate', label: () => 'Today\'s Collection Report', path: '/reports/collection-report' },
  // Document number patterns: "SI 001", "PI-2024-001", "PO 123", etc.
  { pattern: /^(SI|PI|PO|SO|QT|DC|SR|PR|GRN|CR|BR|CP|BP|JV|PS)[- ]?(.+)$/i, action: 'document', label: (m) => `Open ${m[1].toUpperCase()}-${m[2]}` },
  // "new sale" / "new invoice" / "new purchase" shortcuts
  { pattern: /^new\s+(sale|invoice|bill)$/i, action: 'navigate', label: () => 'New Sale Invoice', path: '/sales/sale-invoice' },
  { pattern: /^new\s+purchase$/i, action: 'navigate', label: () => 'New Purchase Invoice', path: '/purchases/purchase-invoice' },
  { pattern: /^new\s+receipt$/i, action: 'navigate', label: () => 'New Cash Receipt', path: '/receipts/cash-receipt' },
  { pattern: /^new\s+payment$/i, action: 'navigate', label: () => 'New Cash Payment', path: '/payments/cash-payment' },
  { pattern: /^new\s+quotation$/i, action: 'navigate', label: () => 'New Quotation', path: '/sales/quotation' },
  { pattern: /^new\s+(journal|jv)$/i, action: 'navigate', label: () => 'New Journal Voucher', path: '/journal/general' },
  { pattern: /^new\s+customer$/i, action: 'navigate', label: () => 'New Customer', path: '/master/customers' },
  { pattern: /^new\s+vendor$/i, action: 'navigate', label: () => 'New Vendor', path: '/master/vendors' },
  { pattern: /^new\s+item$/i, action: 'navigate', label: () => 'New Inventory Item', path: '/master/chart-of-inventory' },
];

// Map document type codes to their transaction pages
const DOC_TYPE_ROUTES = {
  SI: '/sales/sale-invoice', PI: '/purchases/purchase-invoice',
  PO: '/purchases/purchase-order', SO: '/sales/sale-order',
  QT: '/sales/quotation', DC: '/sales/delivery-challan',
  SR: '/sales/sale-return', PR: '/purchases/purchase-return',
  GRN: '/purchases/grn', CR: '/receipts/cash-receipt',
  BR: '/receipts/bank-receipt', CP: '/payments/cash-payment',
  BP: '/payments/bank-payment', JV: '/journal/general',
  PS: '/pos',
};

const RECENT_SEARCHES_KEY = 'amalerp_recent_searches';

const getRecentSearches = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
  } catch { return []; }
};

const saveRecentSearch = (item) => {
  try {
    const recent = getRecentSearches().filter(r => r.path !== item.path || r.label !== item.label);
    recent.unshift({ label: item.label, path: item.path, category: item.category });
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, 8)));
  } catch { /* ignore */ }
};

const ExecutiveSummary = ({ healthScore = 78, healthMetrics = {}, healthInsights = [], healthScoreBreakdown = null, userName = 'Admin', onThemeToggle, isDarkMode, showHealthGauge = true }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [entityCache, setEntityCache] = useState({ customers: [], vendors: [], accounts: [], items: [] });
  const [entityLoaded, setEntityLoaded] = useState(false);
  const [showHealthTooltip, setShowHealthTooltip] = useState(false);
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);

  const navigate = useNavigate();
  const { activeCompany } = useAuth();

  const {
    notifications,
    unreadNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotification
  } = useDashboard();

  const { exportToPDF, exporting } = useExportPDF();

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load entity data for smart search (customers, vendors, accounts) once on first open
  useEffect(() => {
    if (!showSearch || entityLoaded || !activeCompany?.id) return;

    const loadEntities = async () => {
      try {
        const companyId = activeCompany.id;
        // Firebase cost: these four lists now come from the device's local
        // master cache — 0 reads instead of ~1,200 raw document reads on every
        // dashboard visit where search is opened.
        // The stock lookup that used to sit here is GONE: it pulled the
        // company's ENTIRE stock history (~100,000 rows on a large company)
        // purely to print a quantity in a search row.
        const [customerRows, vendorRows, accountRows, itemRows] = await Promise.all([
          getCustomersCached(companyId),
          getVendorsCached(companyId),
          getAccountsCached(companyId),
          getItemsCached(companyId),
        ]);

        const items = (itemRows || []).slice(0, 500).map(data => ({
          id: data.id,
          name: data.itemName || data.name || '',
          code: data.itemCode || data.code || '',
          salePrice: data.salePrice || 0,
          purchasePrice: data.purchasePrice || 0,
          unitOfMeasure: data.unitOfMeasure || '',
        }));

        // Parties store their display name in `companyName`; `name` is the
        // fallback for older rows.
        const party = (d) => ({ id: d.id, name: d.companyName || d.name || '', code: d.code || '' });

        setEntityCache({
          customers: (customerRows || []).slice(0, 200).map(party),
          vendors: (vendorRows || []).slice(0, 200).map(party),
          accounts: (accountRows || []).slice(0, 300).map(d => ({ id: d.id, name: d.name || d.accountName || '', code: d.code || '' })),
          items,
        });
        setEntityLoaded(true);
      } catch (err) {
        console.error('Search: failed to load entities', err);
      }
    };

    loadEntities();
  }, [showSearch, entityLoaded, activeCompany?.id]);

  // ── Search logic ──────────────────────────────────────
  const searchResults = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) {
      // Show recent searches when empty
      const recent = getRecentSearches();
      if (recent.length > 0) {
        return [{ category: 'Recent', items: recent.map(r => ({ ...r, type: 'recent' })) }];
      }
      // Show suggested shortcuts
      return [{
        category: 'Quick Tips',
        items: [
          { label: 'Type a page name to navigate...', type: 'hint', hint: 'e.g. "sale invoice"' },
          { label: '"Shahid ledger" → open account ledger', type: 'hint', hint: 'name + ledger' },
          { label: '"today sale report" → sale register', type: 'hint', hint: 'smart shortcuts' },
          { label: '"SI 001" → find sale invoice #001', type: 'hint', hint: 'doc number' },
          { label: '"new sale" → create new sale invoice', type: 'hint', hint: 'quick create' },
        ]
      }];
    }

    const qLower = q.toLowerCase();
    const results = [];

    // 1) Check smart patterns first
    const smartResults = [];
    for (const sp of SMART_PATTERNS) {
      const match = q.match(sp.pattern);
      if (match) {
        if (sp.action === 'document') {
          const typeCode = match[1].toUpperCase();
          const route = DOC_TYPE_ROUTES[typeCode];
          if (route) {
            smartResults.push({
              label: sp.label(match),
              path: route,
              category: 'Smart Shortcut',
              type: 'smart',
              icon: 'arrow-right',
              state: { searchDocNumber: match[2].trim() }
            });
          }
        } else if (sp.action === 'ledger') {
          // Search entities for the name
          const nameQuery = match[1].toLowerCase();
          const matchedAccounts = entityCache.accounts.filter(a =>
            a.name?.toLowerCase().includes(nameQuery)
          );
          const matchedCustomers = entityCache.customers.filter(c =>
            c.name?.toLowerCase().includes(nameQuery)
          );
          const matchedVendors = entityCache.vendors.filter(v =>
            v.name?.toLowerCase().includes(nameQuery)
          );

          [...matchedAccounts.slice(0, 3), ...matchedCustomers.slice(0, 3), ...matchedVendors.slice(0, 3)].forEach(entity => {
            smartResults.push({
              label: `${entity.name} - Ledger`,
              path: '/reports/ledger',
              category: 'Smart Shortcut',
              type: 'smart',
              icon: 'bar-chart',
              state: { filterAccountId: entity.id }
            });
          });

          // If no entity found, still offer to go to ledger page
          if (smartResults.length === 0) {
            smartResults.push({
              label: sp.label(match),
              path: sp.path,
              category: 'Smart Shortcut',
              type: 'smart',
              icon: 'bar-chart',
            });
          }
        } else {
          smartResults.push({
            label: sp.label(match),
            path: sp.path,
            category: 'Smart Shortcut',
            type: 'smart',
            icon: 'arrow-right',
          });
        }
      }
    }
    if (smartResults.length > 0) {
      results.push({ category: 'Smart Shortcut', items: smartResults });
    }

    // 2) Search static index (pages/routes)
    const pageResults = SEARCH_INDEX.filter(item => {
      const labelMatch = item.label.toLowerCase().includes(qLower);
      const keywordMatch = item.keywords.some(kw => kw.includes(qLower));
      // Also match if query words are all present in keywords
      const words = qLower.split(/\s+/);
      const allWordsMatch = words.length > 1 && words.every(w =>
        item.label.toLowerCase().includes(w) || item.keywords.some(kw => kw.includes(w))
      );
      return labelMatch || keywordMatch || allWordsMatch;
    }).slice(0, 12);

    if (pageResults.length > 0) {
      // Group by category
      const grouped = {};
      pageResults.forEach(item => {
        if (!grouped[item.category]) grouped[item.category] = [];
        grouped[item.category].push({ ...item, type: 'page' });
      });
      Object.entries(grouped).forEach(([cat, items]) => {
        results.push({ category: cat, items });
      });
    }

    // 3) Search entities (customers, vendors, accounts) if query is 2+ chars
    if (qLower.length >= 2) {
      const entityResults = [];

      entityCache.customers
        .filter(c => c.name?.toLowerCase().includes(qLower) || c.code?.toLowerCase().includes(qLower))
        .slice(0, 4)
        .forEach(c => entityResults.push({
          label: c.name, sublabel: `Customer${c.code ? ` (${c.code})` : ''}`,
          path: '/reports/ledger', type: 'entity', icon: 'users',
          category: 'Customers',
          state: { filterAccountId: c.id }
        }));

      entityCache.vendors
        .filter(v => v.name?.toLowerCase().includes(qLower) || v.code?.toLowerCase().includes(qLower))
        .slice(0, 4)
        .forEach(v => entityResults.push({
          label: v.name, sublabel: `Vendor${v.code ? ` (${v.code})` : ''}`,
          path: '/reports/ledger', type: 'entity', icon: 'truck',
          category: 'Vendors',
          state: { filterAccountId: v.id }
        }));

      entityCache.accounts
        .filter(a => a.name?.toLowerCase().includes(qLower) || a.code?.toLowerCase().includes(qLower))
        .slice(0, 4)
        .forEach(a => entityResults.push({
          label: `${a.code ? a.code + ' - ' : ''}${a.name}`, sublabel: 'Account',
          path: '/reports/ledger', type: 'entity', icon: 'book',
          category: 'Accounts',
          state: { filterAccountId: a.id }
        }));

      // Inventory Items — show stock, sale price, unit. Click → Inventory Ledger filtered to this item.
      const fmtNum = (n) => Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 2 });
      (entityCache.items || [])
        .filter(it => it.name?.toLowerCase().includes(qLower) || it.code?.toLowerCase().includes(qLower))
        .slice(0, 6)
        .forEach(it => {
          // Stock intentionally not shown here — see loadEntities. Open the
          // item's Inventory Ledger (this row's action) for live quantities.
          const unitLabel = it.unitOfMeasure ? `Unit ${it.unitOfMeasure}` : 'Item';
          const priceLabel = it.salePrice ? ` · Sale ${fmtNum(it.salePrice)}` : '';
          entityResults.push({
            label: `${it.code ? it.code + ' - ' : ''}${it.name}`,
            sublabel: `${unitLabel}${priceLabel}`,
            path: '/reports/inventory-ledger', type: 'entity', icon: 'package',
            category: 'Items',
            state: { itemId: it.id }
          });
        });

      if (entityResults.length > 0) {
        const grouped = {};
        entityResults.forEach(item => {
          if (!grouped[item.category]) grouped[item.category] = [];
          grouped[item.category].push(item);
        });
        Object.entries(grouped).forEach(([cat, items]) => {
          results.push({ category: cat, items });
        });
      }
    }

    return results;
  }, [searchQuery, entityCache]);

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    const flat = [];
    searchResults.forEach(group => {
      group.items.forEach(item => {
        if (item.type !== 'hint') flat.push(item);
      });
    });
    return flat;
  }, [searchResults]);

  // Handle selecting a result
  const handleSelectResult = useCallback((item) => {
    if (item.type === 'hint') return;

    saveRecentSearch(item);
    setShowSearch(false);
    setSearchQuery('');
    setSelectedIndex(0);

    if (item.state) {
      navigate(item.path, { state: item.state });
    } else {
      navigate(item.path);
    }
  }, [navigate]);

  // Keyboard shortcut for search (Ctrl+K) + arrow navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }

      if (!showSearch) {
        if (e.key === 'Escape') {
          setShowNotifications(false);
        }
        return;
      }

      if (e.key === 'Escape') {
        setShowSearch(false);
        setSearchQuery('');
        setSelectedIndex(0);
        setShowNotifications(false);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && flatResults.length > 0) {
        e.preventDefault();
        handleSelectResult(flatResults[selectedIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, flatResults, selectedIndex, handleSelectResult]);

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Scroll selected result into view
  useEffect(() => {
    if (resultsRef.current) {
      const selected = resultsRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Get icon component for a result
  const getResultIcon = (iconName) => {
    const iconMap = {
      'home': <FiHome />, 'settings': <FiSettings />, 'users': <FiUsers />,
      'book': <FiBook />, 'dollar': <FiDollarSign />, 'package': <FiPackage />,
      'truck': <FiTruck />, 'bar-chart': <FiBarChart2 />, 'credit-card': <FiCreditCard />,
      'file': <FiFileText />, 'grid': <FiGrid />, 'map-pin': <FiMapPin />,
      'briefcase': <FiBriefcase />, 'user-check': <FiUserCheck />,
      'arrow-right': <FiArrowRight />, 'info': <FiInfo />, 'layers': <FiLayers />,
    };
    return iconMap[iconName] || <FiSearch />;
  };

  const handleExportPDF = useCallback(async () => {
    await exportToPDF('dashboard-content', {
      title: 'ERP Dashboard Report',
      companyName: 'AMAL ERP'
    });
  }, [exportToPDF]);

  const getHealthScoreColor = (score) => {
    if (score >= 80) return colors.success.main;
    if (score >= 60) return colors.warning.main;
    return colors.error.main;
  };

  const getHealthScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  const healthBreakdown = useMemo(() => {
    const m = healthMetrics;
    const items = [];

    if (m.currentRatio !== undefined) {
      const s = Math.min(100, (m.currentRatio / 1.5) * 100);
      items.push({ label: 'Current Ratio', value: `${(m.currentRatio || 0).toFixed(2)}`, target: '>= 1.50', score: Math.round(s), weight: '15%' });
    }
    if (m.quickRatio !== undefined) {
      const s = Math.min(100, (m.quickRatio / 1.0) * 100);
      items.push({ label: 'Quick Ratio', value: `${(m.quickRatio || 0).toFixed(2)}`, target: '>= 1.00', score: Math.round(s), weight: '10%' });
    }
    if (m.debtToEquityRatio !== undefined) {
      const s = Math.max(0, 100 - (m.debtToEquityRatio / 1.5) * 100);
      items.push({ label: 'Debt to Equity', value: `${(m.debtToEquityRatio || 0).toFixed(2)}`, target: '< 1.50', score: Math.round(s), weight: '15%' });
    }
    if (m.grossProfitMargin !== undefined) {
      const s = Math.min(100, (m.grossProfitMargin / 50) * 100);
      items.push({ label: 'Gross Profit Margin', value: `${(m.grossProfitMargin || 0).toFixed(1)}%`, target: '50%', score: Math.round(s), weight: '15%' });
    }
    if (m.netProfitMargin !== undefined) {
      const s = Math.min(100, (m.netProfitMargin / 20) * 100);
      items.push({ label: 'Net Profit Margin', value: `${(m.netProfitMargin || 0).toFixed(1)}%`, target: '20%', score: Math.round(s), weight: '15%' });
    }
    if (m.inventoryTurnover !== undefined) {
      const s = m.inventoryTurnover >= 6 && m.inventoryTurnover <= 12 ? 100 : Math.min(100, (m.inventoryTurnover / 6) * 100);
      items.push({ label: 'Inventory Turnover', value: `${(m.inventoryTurnover || 0).toFixed(1)}x`, target: '6-12x', score: Math.round(s), weight: '10%' });
    }
    if (m.dso !== undefined) {
      const s = Math.max(0, 100 - (m.dso / 45) * 100);
      items.push({ label: 'Days Sales Outstanding', value: `${Math.round(m.dso || 0)} days`, target: '< 45 days', score: Math.round(s), weight: '10%' });
    }
    if (m.orderFulfillmentRate !== undefined) {
      items.push({ label: 'Order Fulfillment', value: `${(m.orderFulfillmentRate || 0).toFixed(1)}%`, target: '100%', score: Math.round(m.orderFulfillmentRate || 0), weight: '5%' });
    }
    if (m.customerSatisfactionScore !== undefined) {
      const s = ((m.customerSatisfactionScore || 0) / 5) * 100;
      items.push({ label: 'Customer Satisfaction', value: `${(m.customerSatisfactionScore || 0).toFixed(1)}/5`, target: '5/5', score: Math.round(s), weight: '5%' });
    }

    return items;
  }, [healthMetrics]);

  const getMetricScoreColor = (score) => {
    if (score >= 80) return colors.success.main;
    if (score >= 50) return colors.warning.main;
    return colors.error.main;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'alert':
      case 'error':
        return <FiAlertCircle />;
      case 'warning':
        return <FiClock />;
      case 'success':
        return <FiCheck />;
      default:
        return <FiInfo />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'alert':
      case 'error':
        return colors.error.main;
      case 'warning':
        return colors.warning.main;
      case 'success':
        return colors.success.main;
      default:
        return colors.info.main;
    }
  };

  return (
    <Container
      variants={cardVariants}
      initial="initial"
      animate="animate"
    >
      {/* Left Section - Time & Greeting */}
      <LeftSection>
        <TimeDisplay>
          <CurrentTime>{format(currentTime, 'HH:mm')}</CurrentTime>
          <CurrentDate>{format(currentTime, 'EEEE, dd MMMM yyyy')}</CurrentDate>
        </TimeDisplay>
        <Greeting>
          {getTimeBasedGreeting()}, <strong>{userName}</strong>!
        </Greeting>
      </LeftSection>

      {/* Center Section - Health Score. Hidden since D2 (2026-07-17): the
          business-health computation was removed with its widget; without it
          the gauge would show a made-up default number. */}
      {showHealthGauge && (
      <CenterSection
        onMouseEnter={() => setShowHealthTooltip(true)}
        onMouseLeave={() => setShowHealthTooltip(false)}
      >
        <HealthGauge>
          <CircularProgressbar
            value={healthScore}
            text={`${healthScore}`}
            styles={buildStyles({
              textSize: '24px',
              textColor: colors.text.primary,
              pathColor: getHealthScoreColor(healthScore),
              trailColor: colors.neutral[200],
              pathTransitionDuration: 1.5
            })}
          />
        </HealthGauge>
        <HealthInfo>
          <HealthLabel>Business Health</HealthLabel>
          <HealthStatus $color={getHealthScoreColor(healthScore)}>
            {getHealthScoreLabel(healthScore)}
          </HealthStatus>
        </HealthInfo>

        <AnimatePresence>
          {showHealthTooltip && healthBreakdown.length > 0 && (
            <HealthTooltip
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
            >
              <TooltipHeader>
                <TooltipTitle>Business Health Breakdown</TooltipTitle>
                <TooltipScore $color={getHealthScoreColor(healthScore)}>{healthScore}/100</TooltipScore>
              </TooltipHeader>
              <TooltipDivider />

              {/* Real composite breakdown from buildFinancialHealth */}
              {healthScoreBreakdown ? (
                <TooltipGrid>
                  <TooltipGridHeader>
                    <span>Component</span>
                    <span>Weight</span>
                    <span></span>
                    <span>Score</span>
                  </TooltipGridHeader>
                  {[
                    { label: 'Liquidity', val: healthScoreBreakdown.liquidity, weight: '25%' },
                    { label: 'Profitability', val: healthScoreBreakdown.profitability, weight: '25%' },
                    { label: 'Solvency', val: healthScoreBreakdown.solvency, weight: '20%' },
                    { label: 'Cash Flow', val: healthScoreBreakdown.cashFlow, weight: '15%' },
                    { label: 'AR Efficiency', val: healthScoreBreakdown.arEfficiency, weight: '15%' },
                  ].map((item, i) => (
                    <TooltipRow key={i}>
                      <TooltipMetricName>{item.label}</TooltipMetricName>
                      <TooltipMetricValue>{item.weight}</TooltipMetricValue>
                      <TooltipMetricTarget></TooltipMetricTarget>
                      <TooltipMetricScore $color={getMetricScoreColor(item.val)}>
                        <TooltipScoreBar>
                          <TooltipScoreFill $width={item.val} $color={getMetricScoreColor(item.val)} />
                        </TooltipScoreBar>
                        {Math.round(item.val)}
                      </TooltipMetricScore>
                    </TooltipRow>
                  ))}
                </TooltipGrid>
              ) : (
                <TooltipGrid>
                  <TooltipGridHeader>
                    <span>Metric</span>
                    <span>Value</span>
                    <span>Target</span>
                    <span>Score</span>
                  </TooltipGridHeader>
                  {healthBreakdown.map((item, i) => (
                    <TooltipRow key={i}>
                      <TooltipMetricName>
                        {item.label}
                        <TooltipWeight>({item.weight})</TooltipWeight>
                      </TooltipMetricName>
                      <TooltipMetricValue>{item.value}</TooltipMetricValue>
                      <TooltipMetricTarget>{item.target}</TooltipMetricTarget>
                      <TooltipMetricScore $color={getMetricScoreColor(item.score)}>
                        <TooltipScoreBar>
                          <TooltipScoreFill $width={item.score} $color={getMetricScoreColor(item.score)} />
                        </TooltipScoreBar>
                        {item.score}
                      </TooltipMetricScore>
                    </TooltipRow>
                  ))}
                </TooltipGrid>
              )}

              {/* Live insights from real transactions */}
              {healthInsights && healthInsights.length > 0 && (
                <>
                  <TooltipDivider />
                  <InsightHeader>Top findings from your data</InsightHeader>
                  <InsightList>
                    {healthInsights.slice(0, 6).map((ins, i) => {
                      const color = ins.severity === 'good' ? colors.success.main
                        : ins.severity === 'bad' ? colors.error.main
                        : ins.severity === 'warn' ? colors.warning.main
                        : colors.info.main;
                      return (
                        <InsightLine key={i} $color={color}>
                          <InsightDot style={{ background: color }} />
                          <span>{ins.text}</span>
                        </InsightLine>
                      );
                    })}
                  </InsightList>
                </>
              )}

              <TooltipFooter>
                Composite of liquidity, profitability, solvency, cash flow & AR efficiency — computed live from your Chart of Accounts.
              </TooltipFooter>
              <TooltipArrow />
            </HealthTooltip>
          )}
        </AnimatePresence>
      </CenterSection>
      )}

      {/* Right Section - Actions */}
      <RightSection>
        {/* Quick Actions */}
        <QuickActions>
          <QuickActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            $primary
            onClick={() => navigate('/sales/sale-invoice')}
          >
            <FiPlus />
            <span>New Sale</span>
          </QuickActionButton>
          <QuickActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/purchases/purchase-invoice')}
          >
            <FiShoppingCart />
            <span>Purchase</span>
          </QuickActionButton>
          <QuickActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/reports/trial-balance')}
          >
            <FiFileText />
            <span>Reports</span>
          </QuickActionButton>
          <QuickActionButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExportPDF}
            disabled={exporting}
          >
            <FiDownload />
            <span>{exporting ? 'Exporting...' : 'Export'}</span>
          </QuickActionButton>
        </QuickActions>

        {/* Icon Actions */}
        <IconActions>
          {/* Search Button */}
          <IconActionButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowSearch(true)}
            title="Search (Ctrl+K)"
          >
            <FiSearch />
            <ShortcutBadge>
              <FiCommand size={10} />K
            </ShortcutBadge>
          </IconActionButton>

          {/* Theme Toggle */}
          <IconActionButton
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onThemeToggle}
            title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
          >
            {isDarkMode ? <FiSun /> : <FiMoon />}
          </IconActionButton>

          {/* Notifications */}
          <NotificationButton
            onClick={() => setShowNotifications(!showNotifications)}
            animate={unreadNotifications > 0 ? 'animate' : ''}
            variants={bellShakeVariants}
          >
            <FiBell />
            {unreadNotifications > 0 && (
              <NotificationBadge
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
              >
                {unreadNotifications}
              </NotificationBadge>
            )}
          </NotificationButton>
        </IconActions>
      </RightSection>

      {/* Command Palette Search */}
      <AnimatePresence>
        {showSearch && (
          <>
            <SearchOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setShowSearch(false); setSearchQuery(''); setSelectedIndex(0); }}
            />
            <SearchModal
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
            >
              <SearchInputWrapper>
                <FiSearch size={20} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder='Search pages, customers, reports... (try "shahid ledger" or "today sale")'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery && (
                  <ClearQueryButton onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}>
                    <FiX size={14} />
                  </ClearQueryButton>
                )}
                <SearchShortcutHint>ESC</SearchShortcutHint>
              </SearchInputWrapper>

              <SearchResults ref={resultsRef}>
                {searchResults.length === 0 ? (
                  <SearchEmpty>
                    <FiSearch size={32} />
                    <span>No results for "{searchQuery}"</span>
                    <SearchEmptyHint>Try searching for a page name, customer, or use shortcuts like "new sale"</SearchEmptyHint>
                  </SearchEmpty>
                ) : (
                  (() => {
                    let flatIndex = -1;
                    return searchResults.map((group, gi) => (
                      <SearchResultGroup key={gi}>
                        <SearchResultLabel>{group.category}</SearchResultLabel>
                        {group.items.map((item, ii) => {
                          if (item.type === 'hint') {
                            return (
                              <SearchHintItem key={ii}>
                                <SearchHintLabel>{item.label}</SearchHintLabel>
                                <SearchHintBadge>{item.hint}</SearchHintBadge>
                              </SearchHintItem>
                            );
                          }
                          flatIndex++;
                          const isSelected = flatIndex === selectedIndex;
                          const currentFlatIndex = flatIndex;
                          return (
                            <SearchResultItem
                              key={ii}
                              $selected={isSelected}
                              data-selected={isSelected}
                              onClick={() => handleSelectResult(item)}
                              onMouseEnter={() => setSelectedIndex(currentFlatIndex)}
                            >
                              <SearchResultIcon $type={item.type}>
                                {item.type === 'recent' ? <FiClock /> : getResultIcon(item.icon)}
                              </SearchResultIcon>
                              <SearchResultText>
                                <SearchResultName>{item.label}</SearchResultName>
                                {item.sublabel && <SearchResultSub>{item.sublabel}</SearchResultSub>}
                              </SearchResultText>
                              {item.type === 'smart' && <SmartBadge>Smart</SmartBadge>}
                              {isSelected && (
                                <SearchResultAction>
                                  <FiCornerDownLeft size={12} />
                                </SearchResultAction>
                              )}
                            </SearchResultItem>
                          );
                        })}
                      </SearchResultGroup>
                    ));
                  })()
                )}
              </SearchResults>

              <SearchFooter>
                <FooterHint><kbd>&uarr;</kbd><kbd>&darr;</kbd> Navigate</FooterHint>
                <FooterHint><kbd>&crarr;</kbd> Open</FooterHint>
                <FooterHint><kbd>esc</kbd> Close</FooterHint>
              </SearchFooter>
            </SearchModal>
          </>
        )}
      </AnimatePresence>

      {/* Notifications Panel */}
      <AnimatePresence>
        {showNotifications && (
          <>
            <NotificationOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowNotifications(false)}
            />
            <NotificationPanel
              variants={drawerVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <NotificationHeader>
                <NotificationTitle>Notifications</NotificationTitle>
                {unreadNotifications > 0 && (
                  <MarkAllRead onClick={markAllNotificationsRead}>
                    Mark all as read
                  </MarkAllRead>
                )}
              </NotificationHeader>
              <NotificationList>
                {notifications.length === 0 ? (
                  <EmptyNotifications>
                    <FiBell size={32} />
                    <p>No notifications</p>
                  </EmptyNotifications>
                ) : (
                  notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      $unread={!notification.read}
                      onClick={() => markNotificationRead(notification.id)}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <NotificationIcon $color={getNotificationColor(notification.type)}>
                        {getNotificationIcon(notification.type)}
                      </NotificationIcon>
                      <NotificationContent>
                        <NotificationItemTitle $unread={!notification.read}>
                          {notification.title}
                        </NotificationItemTitle>
                        <NotificationMessage>
                          {notification.message}
                        </NotificationMessage>
                        <NotificationTime>
                          {formatRelativeDate(notification.timestamp)}
                        </NotificationTime>
                      </NotificationContent>
                      <NotificationClose
                        onClick={(e) => {
                          e.stopPropagation();
                          clearNotification(notification.id);
                        }}
                      >
                        <FiX />
                      </NotificationClose>
                    </NotificationItem>
                  ))
                )}
              </NotificationList>
            </NotificationPanel>
          </>
        )}
      </AnimatePresence>
    </Container>
  );
};

// Styled Components
const Container = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--color-primary-main) 0%, var(--color-primary-dark) 100%);
  padding: ${spacing.lg} ${spacing.xl};
  border-radius: ${borderRadius.lg};
  color: var(--color-text-white);
  position: relative;
  z-index: 1;
  box-shadow: var(--shadow-lg);
  overflow: visible;

  @media (max-width: 1200px) {
    flex-wrap: wrap;
    gap: ${spacing.lg};
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const TimeDisplay = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing.md};
`;

const CurrentTime = styled.span`
  font-size: ${typography.fontSize.h1};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: -1px;
`;

const CurrentDate = styled.span`
  font-size: ${typography.fontSize.sm};
  opacity: 0.9;
`;

const Greeting = styled.p`
  font-size: ${typography.fontSize.md};
  opacity: 0.9;

  strong {
    font-weight: ${typography.fontWeight.semibold};
  }
`;

const CenterSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: rgba(255, 255, 255, 0.1);
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.lg};
  backdrop-filter: blur(10px);
  cursor: default;
`;

const HealthGauge = styled.div`
  width: 70px;
  height: 70px;

  .CircularProgressbar-text {
    font-weight: ${typography.fontWeight.bold};
    fill: var(--color-text-white) !important;
  }

  .CircularProgressbar-trail {
    stroke: rgba(255, 255, 255, 0.2) !important;
  }
`;

const HealthInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HealthLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  opacity: 0.8;
`;

const HealthStatus = styled.span`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
`;

const HealthTooltip = styled(motion.div)`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-background-default);
  border: 1px solid var(--color-border-light);
  border-radius: ${borderRadius.lg};
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  padding: ${spacing.md} ${spacing.lg};
  z-index: 100;
  min-width: 420px;
  color: var(--color-text-primary);
`;

const TooltipArrow = styled.div`
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: var(--color-background-default);
  border-left: 1px solid var(--color-border-light);
  border-top: 1px solid var(--color-border-light);
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.sm};
`;

const TooltipTitle = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
`;

const TooltipScore = styled.div`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.$color};
`;

const TooltipDivider = styled.div`
  height: 1px;
  background: var(--color-border-light);
  margin-bottom: ${spacing.sm};
`;

const TooltipGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TooltipGridHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 70px 70px 80px;
  gap: ${spacing.sm};
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-border-light);
`;

const TooltipRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 70px 70px 80px;
  gap: ${spacing.sm};
  align-items: center;
  padding: 3px 0;
`;

const TooltipMetricName = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TooltipWeight = styled.span`
  font-size: 10px;
  color: var(--color-text-tertiary);
  font-weight: ${typography.fontWeight.regular};
`;

const TooltipMetricValue = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
  text-align: right;
`;

const TooltipMetricTarget = styled.div`
  font-size: ${typography.fontSize.xs};
  color: var(--color-text-tertiary);
  text-align: right;
`;

const TooltipMetricScore = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.$color};
`;

const TooltipScoreBar = styled.div`
  flex: 1;
  height: 4px;
  background: var(--color-neutral-200);
  border-radius: 2px;
  overflow: hidden;
`;

const TooltipScoreFill = styled.div`
  height: 100%;
  width: ${props => props.$width}%;
  background: ${props => props.$color};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const TooltipFooter = styled.div`
  margin-top: ${spacing.sm};
  padding-top: ${spacing.sm};
  border-top: 1px solid var(--color-border-light);
  font-size: 10px;
  color: var(--color-text-tertiary);
  text-align: center;
  font-style: italic;
`;

const InsightHeader = styled.div`
  font-size: 11px;
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: ${spacing.sm};
  margin-bottom: 6px;
`;

const InsightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 240px;
  overflow-y: auto;
`;

const InsightLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--color-text-primary);
  padding: 6px 8px;
  background: ${props => props.$color}10;
  border-left: 2px solid ${props => props.$color};
  border-radius: 3px;
`;

const InsightDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${spacing.sm};

  @media (max-width: 1000px) {
    display: none;
  }
`;

const QuickActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$primary ? 'var(--color-text-white)' : 'rgba(255, 255, 255, 0.15)'};
  color: ${props => props.$primary ? 'var(--color-primary-main)' : 'var(--color-text-white)'};
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$primary ? 'var(--color-neutral-100)' : 'rgba(255, 255, 255, 0.25)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;

const IconActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const IconActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  color: var(--color-text-white);
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  svg {
    font-size: 18px;
  }
`;

const ShortcutBadge = styled.span`
  position: absolute;
  bottom: -2px;
  right: -2px;
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 1px 4px;
  background: var(--color-secondary-main);
  border-radius: 4px;
  font-size: 9px;
  font-weight: ${typography.fontWeight.bold};
`;

const NotificationButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.15);
  color: var(--color-text-white);
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  svg {
    font-size: 18px;
  }
`;

const NotificationBadge = styled(motion.span)`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-error-main);
  color: var(--color-text-white);
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  border-radius: ${borderRadius.round};
`;

// Search Modal (Command Palette)
const SearchOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
`;

const SearchModal = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: 92%;
  max-width: 680px;
  background: var(--color-background-default);
  border-radius: ${borderRadius.lg};
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 160px);
  border: 1px solid var(--color-border-light);
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  padding: 14px ${spacing.lg};
  border-bottom: 1px solid var(--color-border-light);
  flex-shrink: 0;

  > svg:first-child {
    color: var(--color-primary-main);
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 15px;
    color: var(--color-text-primary);
    outline: none;

    &::placeholder {
      color: var(--color-text-tertiary);
      font-size: 14px;
    }
  }
`;

const ClearQueryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-neutral-200);
  color: var(--color-text-secondary);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--color-neutral-300);
  }
`;

const SearchShortcutHint = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  background: var(--color-neutral-100);
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid var(--color-neutral-200);
  flex-shrink: 0;
`;

const SearchResults = styled.div`
  padding: ${spacing.sm};
  overflow-y: auto;
  flex: 1;
  max-height: 420px;
`;

const SearchResultGroup = styled.div`
  margin-bottom: ${spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SearchResultLabel = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.5px;
  margin-bottom: 2px;
  padding: 6px 12px 4px;
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: 9px 12px;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  color: var(--color-text-primary);
  transition: background 0.1s;
  background: ${props => props.$selected ? 'color-mix(in srgb, var(--color-primary-main) 7%, transparent)' : 'transparent'};
  border: 1px solid ${props => props.$selected ? 'color-mix(in srgb, var(--color-primary-main) 15%, transparent)' : 'transparent'};

  &:hover {
    background: var(--color-primary-main)12;
  }
`;

const SearchResultIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.md};
  background: ${props => {
    if (props.$type === 'smart') return 'color-mix(in srgb, var(--color-secondary-main) 8%, transparent)';
    if (props.$type === 'entity') return 'color-mix(in srgb, var(--color-primary-main) 6%, transparent)';
    if (props.$type === 'recent') return 'var(--color-neutral-100)';
    return 'var(--color-neutral-100)';
  }};
  color: ${props => {
    if (props.$type === 'smart') return 'var(--color-secondary-main)';
    if (props.$type === 'entity') return 'var(--color-primary-main)';
    return 'var(--color-text-secondary)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 14px;
`;

const SearchResultText = styled.div`
  flex: 1;
  min-width: 0;
`;

const SearchResultName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchResultSub = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-top: 1px;
`;

const SearchResultAction = styled.div`
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`;

const SmartBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--color-secondary-main);
  background: var(--color-secondary-main)15;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
`;

const SearchHintItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  color: var(--color-text-secondary);
  font-size: 13px;
`;

const SearchHintLabel = styled.span`
  color: var(--color-text-secondary);
`;

const SearchHintBadge = styled.span`
  font-size: 10px;
  color: var(--color-text-tertiary);
  background: var(--color-neutral-100);
  padding: 2px 8px;
  border-radius: 4px;
`;

const SearchEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
  gap: 8px;

  svg {
    opacity: 0.3;
    margin-bottom: 4px;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }
`;

const SearchEmptyHint = styled.div`
  font-size: 12px;
  color: var(--color-text-tertiary);
  text-align: center;
`;

const SearchFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--color-border-light);
  background: var(--color-neutral-50);
  flex-shrink: 0;
`;

const FooterHint = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--color-text-tertiary);

  kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    background: white;
    border: 1px solid var(--color-neutral-300);
    border-radius: 4px;
    font-size: 10px;
    font-family: inherit;
    color: var(--color-text-secondary);
    box-shadow: 0 1px 0 var(--color-neutral-200);
  }
`;

// Notification Panel
const NotificationOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const NotificationPanel = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  height: 100vh;
  background: var(--color-background-default);
  box-shadow: var(--shadow-xl);
  z-index: 1001;
  display: flex;
  flex-direction: column;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.lg};
  border-bottom: 1px solid var(--color-border-light);
`;

const NotificationTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: var(--color-text-primary);
`;

const MarkAllRead = styled.button`
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-size: ${typography.fontSize.sm};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing.md};
`;

const EmptyNotifications = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xl};
  color: var(--color-text-secondary);

  svg {
    opacity: 0.5;
    margin-bottom: ${spacing.md};
  }
`;

const NotificationItem = styled(motion.div)`
  display: flex;
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${props => props.$unread ? 'color-mix(in srgb, var(--color-primary-main) 3%, transparent)' : 'transparent'};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-background-paper);
  }
`;

const NotificationIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${borderRadius.round};
  background: ${props => props.$color + '15'};
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationItemTitle = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${props => props.$unread
    ? typography.fontWeight.semibold
    : typography.fontWeight.medium};
  color: var(--color-text-primary);
  margin-bottom: 2px;
`;

const NotificationMessage = styled.div`
  font-size: ${typography.fontSize.xs};
  color: var(--color-text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotificationTime = styled.div`
  font-size: ${typography.fontSize.xs};
  color: var(--color-text-tertiary);
  margin-top: 4px;
`;

const NotificationClose = styled.button`
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${NotificationItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: var(--color-text-secondary);
  }
`;

export default ExecutiveSummary;
